import { useCallback, useEffect, useRef, useState } from "react";

/**
 * For overlays a parent mounts only while they are needed
 * (`{target ? <Dialog /> : null}`, or a route that renders a panel).
 *
 * The overlay opens itself, and `onClose` runs once its exit animation has
 * finished, so the parent can unmount it without cutting the animation short.
 */
export function useOverlayLifecycle(onClose: () => void) {
  const [open, setOpen] = useState(true);
  const close = useCallback(() => setOpen(false), []);

  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const onExitComplete = useCallback(() => {
    onClose();
    // A parent may decline to close (e.g. its handler is a no-op while a request
    // is in flight). Still being mounted a moment later means exactly that, so
    // come back instead of sitting closed and unreachable.
    setTimeout(() => {
      if (mounted.current) setOpen(true);
    }, 0);
  }, [onClose]);

  return {
    open,
    onOpenChange: setOpen,
    onExitComplete,
    /** Start the animated close from code (e.g. after a successful submit). */
    close,
  };
}
